/**
 * «منشی هوشمند پارسینا» on a phone — the record button (PM-0080).
 *
 * On the desk the secretary is a sidebar row and F1. A phone has neither: the
 * sidebar is the bottom bar, which holds DESTINATIONS (the secretary is part of
 * the shell, not a route), and there is no keyboard. So it is one button, in the
 * thumb's corner above the bar — the inline-end of the screen, which is the left
 * on a Persian or Arabic panel — always one tap from whatever screen is open.
 *
 *   ready      the round button — tap to record;
 *   recording  the same RecordCapsule as the desk: stop, the time, the level, ×;
 *   processing the round button with a spinner — a tap records the next command;
 *   warn       why nothing was recorded, for a few seconds.
 *
 * Placed as InstallPrompt places itself — above the kit's BottomNav, whose height
 * is published as `--app-bottom-nav` — and above the screen's docked CTA bar
 * («ارسال پیام», «ثبت بیمار»…), published by MobileScreen as `--app-screen-cta`:
 * a button over a screen's one primary action would be the worst place for it.
 * While shown it publishes its own room as `--app-fab-space`, which MobileScreen
 * adds to its scroll padding so the last row is never trapped under it. It sits
 * UNDER the install card (z-index 80 against 90) — that offer is brief and
 * dismissible, the secretary is not going anywhere.
 */
import React from 'react';
import { Mic } from 'lucide-react';
import { GRADIENT_BRAND } from '@parsina/framework/tokens';

import { C } from '../constants/theme';
import { useTranslation } from '../i18n';
import { FAB_SPACE_VAR, SCREEN_CTA_VAR } from '../mobile/MobileScreen';
import RecordCapsule, { AMBER, SPIN_CSS } from './RecordCapsule';
import { useSecretaryRecorder } from './SecretaryHost';
import { warnText } from './SecretaryRecorder';

/** The button's size and its margin above whatever it stands on. */
const SIZE = 56;
const GAP = 16;

export default function SecretaryFab({ hidden = false }) {
  const { t } = useTranslation();
  const c = useSecretaryRecorder();
  // A result's own window covers the screen; the button comes back when it closes.
  const away = hidden || !c || Boolean(c.openItem);
  React.useLayoutEffect(() => {
    if (away || typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    root.style.setProperty(FAB_SPACE_VAR, `${SIZE + GAP}px`);
    return () => root.style.removeProperty(FAB_SPACE_VAR);
  }, [away]);
  if (away) return null;

  const place = {
    position: 'fixed',
    zIndex: 80,
    insetInlineEnd: 16,
    // The bar's measured height already includes the home-indicator inset, so
    // it is one OR the other — the inset alone on a surface with no bar.
    bottom: `calc(${GAP}px + max(env(safe-area-inset-bottom, 0px), var(--app-bottom-nav, 0px)) + var(${SCREEN_CTA_VAR}, 0px))`,
  };

  if (c.recording) {
    return (
      <div style={place} data-testid="mobile-secretary-recorder">
        <RecordCapsule controls={c} compact height={52} />
      </div>
    );
  }

  if (c.warn) {
    return (
      <button
        type="button"
        onClick={c.start}
        data-testid="mobile-secretary-fab"
        data-state="warn"
        aria-label={`${warnText(c.warn, t)} · ${t('secretary.recorder.record')}`}
        style={{
          ...place, height: 48, display: 'inline-flex', alignItems: 'center', gap: 8, paddingInline: '10px 16px',
          borderRadius: 999, border: `1px solid ${C.border}`, background: C.card, color: C.textPrimary,
          boxShadow: '0 8px 24px rgba(13, 27, 38, 0.18)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
          whiteSpace: 'nowrap', cursor: 'pointer', touchAction: 'manipulation',
        }}
      >
        <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: 999, display: 'grid', placeItems: 'center', background: 'rgba(255, 179, 64, 0.2)', color: AMBER, fontWeight: 800 }}>!</span>
        <span aria-live="polite">{warnText(c.warn, t)}</span>
      </button>
    );
  }

  const processing = c.pending > 0;
  return (
    <button
      type="button"
      onClick={c.start}
      aria-label={processing ? `${t('secretary.recorder.processing')} · ${t('secretary.recorder.record')}` : t('secretary.recorder.record')}
      title={t('secretary.navLabel')}
      data-testid="mobile-secretary-fab"
      data-state={processing ? 'processing' : 'idle'}
      style={{
        ...place,
        width: SIZE,
        height: SIZE,
        borderRadius: 999,
        border: 0,
        display: 'grid',
        placeItems: 'center',
        background: GRADIENT_BRAND,
        color: '#fff',
        boxShadow: '0 8px 24px rgba(27, 78, 107, 0.35), 0 2px 6px rgba(0, 0, 0, 0.12)',
        cursor: 'pointer',
        touchAction: 'manipulation',
      }}
    >
      <style>{SPIN_CSS}</style>
      {processing ? (
        <span
          aria-hidden="true"
          className="secretary-spin"
          style={{ width: 22, height: 22, borderRadius: 999, boxSizing: 'border-box', border: '2.5px solid rgba(255, 255, 255, 0.35)', borderTopColor: '#fff' }}
        />
      ) : <Mic size={24} aria-hidden="true" />}
    </button>
  );
}
